import { useEffect, useState } from 'react'
import { Field, Form, Formik } from 'formik'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { isEmpty, map } from 'lodash'

import Modal from 'components/Modal'
import ToggleButton from 'components/ToggleButton'
import DayOpeningHours from 'components/DayOpeningHours'
import Merchant from 'components/RevenueCenters/Form/Merchant'
import useEmailAuth from 'hooks/useEmailAuth'

import { getMenu } from 'store/Menus/selectors'
import { addMenuSettings } from 'store/RevenueCenters/actions'
import {
  getMenuSettings,
  getNewCurrentServiceType,
} from 'store/RevenueCenters/selector'
import {
  OPENING_HOURS_OPERATING_HOURS,
  OPENING_HOURS_ORDER_AHEAD_HOURS,
} from 'resources'
import { transformOpeningDays } from 'utils/revenueCenters/transformOpeningDays'
import { transformOpeningDaysOperatingHours } from 'utils/revenueCenters/transformOpeningDaysOperatingHours'

const MenuSettings = () => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)
  const [
    validationErrorOperatingHours,
    setValidationErrorOperatingHours,
  ] = useState({})
  const [
    validationErrorOrderAheadHours,
    setValidationErrorOrderAheadHours,
  ] = useState({})

  const validationErrorOperatingHoursValues = Object.values(
    validationErrorOperatingHours
  )
  const validationErrorOrderAheadHoursValues = Object.values(
    validationErrorOrderAheadHours
  )

  const isDisabled =
    validationErrorOperatingHoursValues.includes(true) ||
    validationErrorOrderAheadHoursValues.includes(true)

  const [operatingHoursToDisplay, setOperatingHoursToDisplay] =
    useState({})

  const [orderAheadHoursToDisplay, setOrderAheadHoursToDisplay] =
    useState({})

  const [operatingHoursToState, setOperatingHoursToState] = useState(
    {}
  )
  const [orderAheadHoursState, setOrderAheadHoursState] = useState({})

  const isVenueNexter = useEmailAuth()

  const { state: locationState } = useLocation()

  const serviceTypeUuid = locationState?.serviceTypeUuid

  const history = useHistory()
  const dispatch = useDispatch()

  const menu = useSelector((state) => getMenu(state, serviceTypeUuid))
  const newCreatedMenus = useSelector((state) =>
    getNewCurrentServiceType(state, serviceTypeUuid)
  )

  // If it is new creted menu, then all stored data is in redux, that's why we should check if it is from api or from redux
  const currentMenus = isEmpty(menu) ? newCreatedMenus : menu

  const storedMenuSettings = useSelector((state) =>
    getMenuSettings(state, serviceTypeUuid)
  )

  const serviceTypeVisible =
    `${currentMenus?.serviceType}`.toLowerCase() + `Visible`

  const isKioskOrPosServiceType = !!(
    currentMenus?.serviceType === 'Kiosk' ||
    currentMenus?.serviceType === 'POS'
  )

  const {
    // Commented for future using. Reason: endpoint `/external/settings` used in Venue Settings does not accurately represent the configured value, ticket: T29403.
    // provideTipSuggestions,
    visible,
    operatingHours,
    orderAheadHours,
    preorder,
    merchantId,
  } = storedMenuSettings

  const fetchedInitialValues = {
    // Commented for future using. Reason: endpoint `/external/settings` used in Venue Settings does not accurately represent the configured value, ticket: T29403.
    // provideTipSuggestions:
    //   currentMenus?.provideTipSuggestions ?? false,
    visible: currentMenus?.[serviceTypeVisible] ?? false,
    operatingHours: currentMenus?.operatingHours ?? {},
    orderAheadHours: currentMenus?.orderAheadHours ?? {},
    uuid: currentMenus?.uuid ?? '',
    preorder: currentMenus?.preorder ?? false,
    merchantId: currentMenus?.merchantId,
  }

  const initialValues = isEmpty(storedMenuSettings)
    ? fetchedInitialValues
    : {
        // Commented for future using. Reason: endpoint `/external/settings` used in Venue Settings does not accurately represent the configured value, ticket: T29403.
        // provideTipSuggestions,
        visible,
        operatingHours,
        orderAheadHours,
        preorder,
        merchantId,
      }

  const onSubmit = ({
    // Commented for future using. Reason: endpoint `/external/settings` used in Venue Settings does not accurately represent the configured value, ticket: T29403.
    // provideTipSuggestions,
    visible,
    preorder,
    merchantId,
  }) => {
    const menuSettingsData = [
      {
        // Commented for future using. Reason: endpoint `/external/settings` used in Venue Settings does not accurately represent the configured value, ticket: T29403.
        // provideTipSuggestions,
        visible,
        preorder,
        merchantId,
        leadingOrderNumberText: preorder,
        operatingHours: operatingHoursToState,
        orderAheadHours: orderAheadHoursState,
        uuid: serviceTypeUuid,
        visibilityName: serviceTypeVisible,
        serviceTypeName: currentMenus?.serviceType,
      },
    ]

    dispatch(addMenuSettings(menuSettingsData))
    history.push({
      state: {
        ...history.location.state,
        modalType: null,
        serviceTypeUuid: null,
      },
    })
  }

  const onCloseModal = () => {
    history.push({
      state: {
        ...history.location.state,
        modalType: null,
        serviceTypeUuid: null,
      },
    })
  }

  const handleChangeBusinessHours = (hours) => {
    const { dayName, startTime, endTime, disabled, type } = hours

    setOperatingHoursToState({
      ...operatingHoursToState,
      [dayName]: [
        {
          startTime: startTime ?? '',
          endTime: endTime ?? '',
          disabled: disabled ?? false,
          type: type ?? '',
        },
      ],
    })
  }

  const handleChangeOrderAheadHours = (hours) => {
    const { dayName, startTime, endTime, disabled, type } = hours

    setOrderAheadHoursState({
      ...orderAheadHoursState,
      [dayName]: {
        startTime: startTime ?? '',
        endTime: endTime ?? '',
        disabled: disabled ?? false,
        type: type ?? '',
      },
    })
  }

  const onClickSave = () => {
    setShowValidationErrors(true)
  }

  // operatingHoursToDisplay and orderAheadHoursToDisplay have to be checked in two ways
  // one way if they are in initial values, else second way if they aren't null in current menu

  useEffect(() => {
    if (!isEmpty(initialValues?.operatingHours)) {
      setOperatingHoursToDisplay(
        transformOpeningDaysOperatingHours(
          initialValues?.operatingHours
        )
      )
    } else {
      isEmpty(currentMenus?.operatingHours)
        ? setOperatingHoursToDisplay(OPENING_HOURS_OPERATING_HOURS)
        : setOperatingHoursToDisplay(currentMenus?.operatingHours)
    }

    if (!isEmpty(initialValues?.orderAheadHours)) {
      setOrderAheadHoursToDisplay(
        transformOpeningDays(initialValues?.orderAheadHours)
      )
    } else {
      isEmpty(currentMenus?.orderAheadHours)
        ? setOrderAheadHoursToDisplay(OPENING_HOURS_ORDER_AHEAD_HOURS)
        : setOrderAheadHoursToDisplay(currentMenus?.orderAheadHours)
    }
  }, [
    initialValues?.operatingHours,
    initialValues?.orderAheadHours,
    currentMenus?.operatingHours,
    currentMenus?.orderAheadHours,
  ])

  useEffect(() => {
    const businessHoursToState = isEmpty(
      initialValues?.operatingHours
    )
      ? OPENING_HOURS_OPERATING_HOURS
      : transformOpeningDaysOperatingHours(
          initialValues?.operatingHours
        )

    const orderAheadHoursToState = isEmpty(
      initialValues?.orderAheadHours
    )
      ? OPENING_HOURS_ORDER_AHEAD_HOURS
      : transformOpeningDays(initialValues?.orderAheadHours)

    setOperatingHoursToState({
      ...businessHoursToState,
    })

    setOrderAheadHoursState({
      ...orderAheadHoursToState,
    })
  }, [])

  return (
    <Modal
      title="Menu Settings"
      onClose={onCloseModal}
      className="-narrow"
    >
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="item-settings">
                <section
                  key={'menu_merchant'}
                  className="item-settings__section"
                >
                  <Merchant
                    title={'Menu Merchant'}
                    key={'menu_merchant'}
                    id={'menu_merchant'}
                  />
                </section>
                <section>
                  {/* // Commented for future using. Reason: endpoint `/external/settings` used in Menu Settings does not accurately represent the configured value, ticket: T29403. */}
                  {/* <div className="content-wrapper -more-space">
                    <div className="content-header">
                      Tip Suggestions
                    </div>
                    <Field
                      component={ToggleButton}
                      name="provideTipSuggestions"
                      value={values?.provideTipSuggestions}
                      onChange={(val) =>
                        setFieldValue('provideTipSuggestions', val)
                      }
                    />
                  </div> */}
                  <div className="content-wrapper -more-space">
                    <div className="content-header ">Visibility</div>
                    <Field
                      component={ToggleButton}
                      name="visible"
                      value={values?.visible}
                      onChange={(val) =>
                        setFieldValue('visible', val)
                      }
                    />
                  </div>
                  <div className="content-wrapper -more-space">
                    <div className="content-header ">
                      Pre-Order Menu
                    </div>
                    <Field
                      component={ToggleButton}
                      name="preorder"
                      value={values?.preorder}
                      onChange={(val) =>
                        setFieldValue('preorder', val)
                      }
                    />
                  </div>
                </section>
                <section>
                  <div className="content-wrapper -block">
                    <h3 className="header-title">Business Hours</h3>
                    <p className="modal-paragraph -more-space">
                      Time in 24-hour format
                    </p>

                    {map(
                      operatingHoursToDisplay,
                      (operateHour, idx) => {
                        return (
                          <DayOpeningHours
                            key={idx}
                            dayName={idx}
                            activeDay={
                              operateHour[0]?.disabled ?? true
                            }
                            startTime={operateHour[0]?.startTime}
                            endTime={operateHour[0]?.endTime}
                            type={operateHour[0]?.type}
                            handleChange={handleChangeBusinessHours}
                            showValidationErrors={
                              showValidationErrors
                            }
                            setValidationErrorsHours={
                              setValidationErrorOperatingHours
                            }
                            validationErrorsHours={
                              validationErrorOperatingHours
                            }
                          />
                        )
                      }
                    )}
                  </div>
                </section>
                <section>
                  <div className="content-wrapper -block -more-space">
                    <h3 className="header-title">
                      Order Ahead Hours
                    </h3>
                    <p className="modal-paragraph -more-space">
                      Time in 24-hour format
                    </p>
                    {map(
                      orderAheadHoursToDisplay,
                      (
                        { disabled, startTime, endTime, type },
                        idx
                      ) => {
                        return (
                          <DayOpeningHours
                            key={idx}
                            dayName={idx}
                            activeDay={disabled ?? true}
                            startTime={startTime}
                            endTime={endTime}
                            type={type}
                            handleChange={handleChangeOrderAheadHours}
                            showValidationErrors={
                              showValidationErrors
                            }
                            setValidationErrorsHours={
                              setValidationErrorOrderAheadHours
                            }
                            validationErrorsHours={
                              validationErrorOrderAheadHours
                            }
                          />
                        )
                      }
                    )}
                  </div>
                </section>
                <div className="btn-wrapper">
                  <button
                    className="btn -update"
                    type="submit"
                    onClick={onClickSave}
                    disabled={showValidationErrors && isDisabled}
                  >
                    Save
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default MenuSettings
